import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { FiTrendingDown, FiDollarSign, FiRepeat, FiCreditCard, FiUsers } from 'react-icons/fi';
import AppLayout from '../../components/layout/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import { Card, CardHeader, CardBody } from '../../components/ui/Card';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorBanner from '../../components/ui/ErrorBanner';
import { getExpenseDashboard } from '../../api/expenses';

const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmt(v) {
  if (!v) return '₹0';
  if (v >= 1e7) return '₹' + (v / 1e7).toFixed(2) + ' Cr';
  if (v >= 1e5) return '₹' + (v / 1e5).toFixed(2) + ' L';
  if (v >= 1000) return '₹' + (v / 1000).toFixed(1) + ' K';
  return '₹' + v.toLocaleString('en-IN');
}

export default function ExpenseDashboard() {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const { data: dashData, isLoading, error } = useQuery({
    queryKey: ['expense', 'dashboard', currentYear],
    queryFn: () => getExpenseDashboard(currentYear),
  });

  if (isLoading) {
    return (
      <AppLayout title="Expenses" breadcrumb="Expenses">
        <LoadingSpinner label="Loading expense data..." />
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout title="Expenses" breadcrumb="Expenses">
        <ErrorBanner error={error} title="Failed to load expense data" />
      </AppLayout>
    );
  }

  const chartData = (dashData.monthlyData || []).map(d => ({
    name: SHORT[d.month - 1],
    Salary: d.salary,
    Subscriptions: d.subscriptions,
    Reimbursements: d.reimbursements,
    Others: d.otherExpenses,
    total: d.total
  }));

  return (
    <AppLayout title="Expense Dashboard" breadcrumb="Finance > Expenses">
      <div className="space-y-6">
        <PageHeader
          title="Expense Dashboard"
          description={`Comprehensive tracking of corporate expenses and salary costs for ${currentYear}`}
          actions={
            <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm">
              Fiscal Year {currentYear}
            </div>
          }
        />

        {/* TOP METRICS GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Expense</span>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <FiTrendingDown className="text-base" />
              </div>
            </div>
            <div className="text-xl font-bold text-slate-900">{fmt(dashData.totalExpense)}</div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Salary Costs</span>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <FiUsers className="text-base" />
              </div>
            </div>
            <div className="text-xl font-bold text-slate-900">{fmt(dashData.totalSalary)}</div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Subscriptions</span>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <FiRepeat className="text-base" />
              </div>
            </div>
            <div className="text-xl font-bold text-slate-900">{fmt(dashData.totalSubscriptions)}</div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reimbursements</span>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <FiDollarSign className="text-base" />
              </div>
            </div>
            <div className="text-xl font-bold text-slate-900">{fmt(dashData.totalReimbursements)}</div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Other Expenses</span>
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <FiCreditCard className="text-base" />
              </div>
            </div>
            <div className="text-xl font-bold text-slate-900">{fmt(dashData.totalOtherExpenses)}</div>
          </div>
        </div>

        {/* CHART AREA CARD */}
        <Card>
          <CardHeader
            title="Monthly Expense Breakdown"
            subtitle={`Categorized operational expenditure for ${currentYear}`}
            actions={
              <div className="flex items-center gap-3 text-xs font-semibold">
                <span className="inline-flex items-center gap-1 text-slate-700">
                  <span className="h-2.5 w-2.5 rounded-full bg-blue-800"></span> Salary
                </span>
                <span className="inline-flex items-center gap-1 text-slate-700">
                  <span className="h-2.5 w-2.5 rounded-full bg-blue-600"></span> Subscriptions
                </span>
                <span className="inline-flex items-center gap-1 text-slate-700">
                  <span className="h-2.5 w-2.5 rounded-full bg-blue-400"></span> Reimbursements
                </span>
                <span className="inline-flex items-center gap-1 text-slate-700">
                  <span className="h-2.5 w-2.5 rounded-full bg-slate-600"></span> Others
                </span>
              </div>
            }
          />
          <CardBody>
            <div className="h-72 w-full pt-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748B', fontSize: 12 }}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#64748B', fontSize: 12 }}
                    tickFormatter={(v) => {
                      if (v >= 1e7) return (v / 1e7).toFixed(1) + 'Cr';
                      if (v >= 1e5) return (v / 1e5).toFixed(0) + 'L';
                      if (v >= 1000) return (v / 1000).toFixed(0) + 'k';
                      return v;
                    }}
                  />
                  <RechartsTooltip 
                    cursor={{ fill: '#F1F5F9' }}
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '10px', color: '#0F172A', fontSize: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  />
                  <Bar dataKey="Salary" stackId="a" fill="#1D4ED8" />
                  <Bar dataKey="Subscriptions" stackId="a" fill="#2563EB" />
                  <Bar dataKey="Reimbursements" stackId="a" fill="#60A5FA" />
                  <Bar dataKey="Others" stackId="a" fill="#64748B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>

        {/* MONTHLY TABLE CARD */}
        <Card>
          <CardHeader
            title="Monthly Details"
            subtitle="Detailed statement per calendar month"
          />
          <CardBody className="p-0 overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold uppercase tracking-wider">
                  <th className="py-3 px-6">Month</th>
                  <th className="py-3 px-6 text-right">Salary</th>
                  <th className="py-3 px-6 text-right">Subscriptions</th>
                  <th className="py-3 px-6 text-right">Reimbursements</th>
                  <th className="py-3 px-6 text-right">Other Expenses</th>
                  <th className="py-3 px-6 text-right font-bold text-slate-900">Total Expense</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {chartData.map((row, i) => (
                  <tr
                    key={i}
                    className="hover:bg-slate-50/80 transition-colors cursor-pointer text-slate-800"
                    onClick={() => navigate(`/finance/expenses?month=${i + 1}&year=${currentYear}`)}
                  >
                    <td className="py-3.5 px-6 font-semibold text-slate-900">{row.name} {currentYear}</td>
                    <td className="py-3.5 px-6 text-right text-slate-700">{fmt(row.Salary)}</td>
                    <td className="py-3.5 px-6 text-right text-slate-700">{fmt(row.Subscriptions)}</td>
                    <td className="py-3.5 px-6 text-right text-slate-700">{fmt(row.Reimbursements)}</td>
                    <td className="py-3.5 px-6 text-right text-slate-700">{fmt(row.Others)}</td>
                    <td className="py-3.5 px-6 text-right font-bold text-slate-900 bg-slate-50/50">{fmt(row.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      </div>
    </AppLayout>
  );
}
